import PolicyDocPage from "@/components/PolicyDocPage";

const SECTIONS = [
  {
    n: 1,
    title: "Introduction and Applicability",
    paragraphs: [
      'These Standard Terms and Conditions ("Standard Terms") govern the grant of loan facility ("Facility" or "Loan") by Sahyog Finance, Vyara ("Lender", "we", "us" or "our") to the Borrower ("you" or "Borrower"). The Facility is provided subject to these Standard Terms read together with the Application Form, Drawdown Request(s), Most Important Terms and Conditions (MITC), and other documents exchanged between the parties (collectively, the "Transaction Documents").',
      "By submitting the Application Form or accepting any disbursement, the Borrower agrees to be bound by these Standard Terms. Acceptance of the application does not create any obligation on the Lender to sanction or disburse the Loan.",
    ],
  },
  {
    n: 2,
    title: "Definitions",
    paragraphs: ["In these Standard Terms, unless the context otherwise requires:"],
    bullets: [
      '<strong class="text-white">Account</strong> — the bank account designated by the Borrower for disbursement of the Loan.',
      '<strong class="text-white">Application Form</strong> — the loan application form with all supporting documents, KYC details, declarations and clarifications.',
      '<strong class="text-white">Availability Period</strong> — one (1) month from the date of sanction (or such extended period as may be approved by the Lender).',
      '<strong class="text-white">Business Day</strong> — a day (other than Saturday or Sunday) on which banks are open for general business in the State of Gujarat.',
      '<strong class="text-white">Credit Limit</strong> — maximum drawdown limit granted by the Lender under a revolving credit line.',
      '<strong class="text-white">Drawdown Request</strong> — a request for disbursement of the Loan in the form acceptable to the Lender.',
      '<strong class="text-white">Due Date</strong> — date(s) on which any payment becomes due and payable under the Transaction Documents.',
      '<strong class="text-white">Event of Default</strong> — has the meaning ascribed in Clause 7.',
      '<strong class="text-white">Facility</strong> — the loan facility applied for and/or sanctioned, including any revolving credit line.',
      '<strong class="text-white">Increased Costs</strong> — any reduction in return on the Loan or additional regulatory/provisioning cost.',
      '<strong class="text-white">Outstanding Balance</strong> — aggregate of principal, interest, charges, costs, expenses and all other sums payable by the Borrower.',
      '<strong class="text-white">Prepayment Charges</strong> — charges payable on prepayment, calculated as the difference between New and Existing Outstanding Balance.',
      '<strong class="text-white">Tax</strong> — any tax (including GST), levy, duty, withholding or similar charge, together with any penalty or interest thereon.',
    ],
  },
  {
    n: 3,
    title: "Grant of Facility and Disbursement",
    paragraphs: [
      "3.1 The Lender may, at its sole and absolute discretion, sanction the Facility based on the Application Form, KYC documents, credit reports and other Transaction Documents. The Lender is under no obligation to sanction any Loan or provide written acceptance of any Drawdown Request.",
      "3.2 Disbursement shall be made only if (a) no Event of Default has occurred; (b) the Drawdown Request is within the Availability Period and Credit Limit; and (c) no material adverse change has occurred in the opinion of the Lender.",
      "3.3 The Loan shall be disbursed into the Account specified by the Borrower. The Lender shall not be responsible for any third-party dispute regarding the use or disbursement of the Loan.",
      "3.4 The Borrower shall utilise the Loan strictly for the purpose stated in the Application Form and in accordance with applicable laws. The proceeds shall not be used for purchase of gold or any illegal/prohibited purpose.",
    ],
  },
  {
    n: 4,
    title: "Interest, Charges and Repayment",
    paragraphs: [
      "4.1 The rate of interest applicable to the Facility shall be as specified in the Transaction Documents. The Borrower acknowledges that the interest rates are reasonable and represent a genuine pre-estimate of the loss likely to be suffered by the Lender in case of default.",
      "4.2 Interest rates may be revised prospectively by the Lender in accordance with changes in monetary policy, market conditions or internal policies. The Borrower shall bear all applicable Taxes (including GST) on charges and fees.",
      "4.3 All payments shall be made on the Due Date. If the Due Date is not a Business Day, payment shall be made on the immediately preceding Business Day. Payments shall be in freely transferable funds without any set-off, counterclaim or deduction (except TDS as required by law).",
      "4.4 The repayment schedule may be in the form of equated monthly instalments (EMI) or any other structure as specified by the Lender. The Lender reserves the right to recall the entire outstanding amount at any time at its sole discretion, with or without assigning any reason.",
    ],
  },
  {
    n: 5,
    title: "Prepayment",
    paragraphs: [
      "The Borrower may prepay the Loan (in full or in part) only with the prior written consent of the Lender and subject to payment of applicable Prepayment Charges as determined by the Lender. The Prepayment Charges shall be calculated based on the difference between the Outstanding Balance before and after prepayment, as per the prevailing repayment schedule.",
    ],
  },
  {
    n: 6,
    title: "Representations and Warranties",
    paragraphs: [
      "The Borrower represents and warrants (which representations shall be deemed repeated on each day during the tenure of the Facility) that:",
    ],
    bullets: [
      "6.1 The Borrower has full legal capacity and authority to enter into and perform the Transaction Documents.",
      "6.2 The Transaction Documents constitute valid, binding and enforceable obligations.",
      "6.3 The Borrower is in compliance with all applicable laws including tax, anti-money laundering, anti-bribery and environment.",
      "6.4 All information provided to the Lender is true, accurate, complete and not misleading.",
      "6.5 The Borrower is not a willful defaulter and no litigation or insolvency proceedings are pending or threatened against the Borrower (except as disclosed in writing).",
    ],
  },
  {
    n: 7,
    title: "Events of Default",
    paragraphs: ['The following events shall constitute an "Event of Default":'],
    bullets: [
      "7.1 Failure to pay any amount due under the Transaction Documents on the Due Date.",
      "7.2 Breach of any representation, warranty, covenant or term (and failure to remedy within 10 days of notice, where capable of remedy).",
      "7.3 Death of the Borrower or blacklisting/suspension/termination of any arrangement with any portal or platform used by the Borrower.",
      "7.4 The Borrower becomes insolvent or unable to pay debts as they fall due, or insolvency proceedings are initiated.",
      "7.5 Any other event which, in the reasonable opinion of the Lender, materially and adversely affects the Borrower's ability to perform obligations.",
    ],
  },
  {
    n: 8,
    title: "Consequences of Event of Default",
    paragraphs: [
      "Upon occurrence of an Event of Default, the Lender may, at its sole discretion and without prejudice to any other rights:",
    ],
    bullets: [
      "(a) declare all outstanding amounts immediately due and payable;",
      "(b) cancel any undisbursed portion of the Facility;",
      "(c) charge default interest / additional interest as specified;",
      "(d) initiate legal proceedings; and",
      "(e) report the default to credit bureaus including TransUnion CIBIL and other statutory authorities.",
    ],
  },
  {
    n: 9,
    title: "Increased Costs and Indemnity",
    paragraphs: [
      "The Borrower shall, on demand, pay to the Lender all Increased Costs incurred by the Lender due to change in law, regulatory requirements, or any other factor beyond the Lender's control. The Borrower shall indemnify and hold the Lender harmless against all losses, costs, damages, expenses, liabilities (including legal fees and tax liabilities) arising out of or in connection with the Facility or any breach of the Transaction Documents.",
    ],
  },
  {
    n: 10,
    title: "Appropriation of Payments",
    paragraphs: [
      "Notwithstanding anything contained in the Indian Contract Act, 1872 or any other law, all amounts received from the Borrower shall be appropriated in the following order: (i) costs, charges and expenses; (ii) interest on costs and expenses; (iii) default interest / additional interest; (iv) normal interest; and (v) principal amount.",
    ],
  },
  {
    n: 11,
    title: "Notices",
    paragraphs: [
      "Any notice or communication shall be in writing and deemed duly received if delivered personally, sent by registered post, or emailed to the addresses below:",
    ],
    sub: [
      { label: "For the Lender", value: "Sahyog Finance, A-106, Ambaji Complex, Near Unai Naka, Unai Road, Vyara, Dist. Tapi, Gujarat – 394650 · sahyogpfir@gmail.com" },
      { label: "For the Borrower", value: "Address and email ID as per the Application Form (or such other address intimated in writing)." },
    ],
  },
  {
    n: 12,
    title: "Arbitration and Jurisdiction",
    paragraphs: [
      "All disputes arising out of or in connection with these Transaction Documents shall be governed by the laws of India and shall be finally settled by arbitration in accordance with the Arbitration and Conciliation Act, 1996 (as amended). The arbitration shall be conducted by a sole arbitrator appointed by the Lender. The seat and venue of arbitration shall be in the State of Gujarat. The language of arbitration shall be English. Subject to the above, the courts at Gujarat shall have exclusive jurisdiction.",
    ],
  },
  {
    n: 13,
    title: "Assignment",
    paragraphs: [
      'The Lender may, at any time and without requiring the prior consent of or notice to the Borrower, sell, assign, transfer, securitise or novate all or any part of its rights, benefits and obligations to any third party ("Assignee"). The Borrower shall continue to be bound by these terms notwithstanding such assignment. The Borrower shall not assign or transfer any of its rights or obligations.',
    ],
  },
  {
    n: 14,
    title: "Online / Digital Facility",
    paragraphs: [
      "The Borrower may apply for and operate the Facility through secure online platforms designated by the Lender. The Borrower shall keep all Access Codes (username, password, OTP, etc.) confidential and shall not share them with any unauthorized person. All transactions carried out using valid Access Codes shall be deemed to have been authorised by the Borrower. The Lender shall not be liable for any loss arising from unauthorised use of Access Codes or technical failures beyond its reasonable control.",
    ],
  },
  {
    n: 15,
    title: "Goods and Services Tax (GST)",
    paragraphs: [
      "All charges, fees and interest mentioned in the Transaction Documents are exclusive of applicable GST unless otherwise stated. If the Borrower is not registered under GST, input tax credit on charges paid shall not be available. The Borrower shall promptly intimate any change in GST registration status to the Lender. In case of any discrepancy resulting in additional tax liability (including interest/penalty), the Borrower shall reimburse the same immediately.",
    ],
  },
  {
    n: 16,
    title: "Grievance Redressal Mechanism",
    sub: [
      { label: "Level 1 — Customer Care", value: "cust.care.sahyogpfir@gmail.com · Written response within 7 business days." },
      { label: "Level 2 — Nodal Officer", value: "Mr. Digant Gamit, Head of Operations · sahyogpfir@gmail.com · Sahyog Finance, A-106, Ambaji Complex, Near Unai Naka, Vyara, Dist. Tapi, Gujarat – 394650" },
      { label: "Level 3 — Ombudsman", value: "If unresolved after one month from first complaint, approach the appropriate Ombudsman / regulatory authority as per applicable guidelines." },
    ],
  },
  {
    n: 17,
    title: "Collection Policy",
    paragraphs: ["In case of delay in repayment:"],
    bullets: [
      'If any EMI remains overdue for more than 30 days, the Loan shall be classified as <strong class="text-white">"Default"</strong>.',
      "Collection agents will contact the Borrower via telephone, email and other registered contact details.",
      "If the Borrower remains uncontactable for more than 15 days after the due date, the Lender reserves the right to contact the reference persons/numbers provided in the Application Form.",
      "After 30 days of continuous default, the Lender may issue a legal notice recalling the entire outstanding amount.",
      'Persistent default or willful non-payment may result in reporting to credit bureaus as <strong class="text-white">"Willful Defaulter"</strong> and initiation of appropriate legal action.',
    ],
  },
  {
    n: 18,
    title: "General Provisions",
    paragraphs: [
      "18.1 The Lender reserves the right to amend these Standard Terms (other than the rate of interest, which requires prior consent of the Borrower) by giving notice. Continued use of the Facility after such notice shall constitute acceptance of the amended terms.",
      "18.2 If any loan remains overdue for more than 30 days, the Lender reserves the right to charge compound interest on the overdue amount for every subsequent period of 30 days or part thereof.",
      "18.3 The Lender may engage third-party collection agencies or delegates to recover dues and may share necessary information with them. The Borrower consents to such delegation.",
      "18.4 These Standard Terms constitute the entire agreement between the parties and supersede all prior discussions and agreements.",
      "18.5 If any provision is held to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.",
    ],
  },
  {
    n: 19,
    title: "Channel-Partner Referral Commissions & Payout Window",
    paragraphs: [
      "19.1 All referral commissions, cascading overrides and performance incentives payable to registered channel partners (Advisor, DSA, Associate Partner, Agency Partner, Sub Branch, Branch) shall be computed in accordance with the commission matrix published on the Referral Payout Calculator and the current channel-partner agreement.",
      "19.2 <strong class=\"text-white\">Payout Window</strong> — Qualifying commissions computed for any given calendar month shall be consolidated, reconciled against actual loan disbursements, and released to the partner's registered bank account <strong class=\"text-white\">between the 1st and the 10th day of the following month</strong>. Any KYC / bank-account discrepancy, pending documentation, or clawback dispute will pause the credit until the file is cleared by the Sahyog Finance channel-partner team.",
      "19.3 <strong class=\"text-white\">Eligibility Threshold</strong> — To be entitled to any monthly payout, the partner must clock the mandatory personal-business volume applicable to their level (DSA ₹20 lakh, Associate Partner ₹25 lakh, Agency Partner ₹35 lakh, Sub Branch ₹45 lakh, Branch ₹70 lakh) in that calendar month. Failure to meet the threshold shall result in the payout being deferred to the next qualifying month.",
      "19.4 All commission amounts are gross of statutory deductions (TDS, GST as applicable) which shall be withheld or invoiced separately in accordance with applicable Indian tax laws.",
      "19.5 The commission matrix and payout window are subject to prospective amendment by the Lender with prior written notice; no retrospective changes shall apply to commissions already accrued.",
    ],
  },
  {
    n: 20,
    title: "Acceptance",
    paragraphs: [
      "By signing the Application Form, submitting a Drawdown Request, or accepting any disbursement under the Facility, the Borrower confirms having read, understood, and agreed to these Standard Terms and Conditions and the Most Important Terms and Conditions (MITC).",
    ],
  },
];

export default function TermsAndConditions() {
  return (
    <PolicyDocPage
      testId="terms-conditions-page"
      eyebrow="Sahyog Finance · Registered Money Lender · Tapi District, Gujarat"
      title={
        <>
          Terms &amp; <em className="not-italic text-[#D4AF37]">Conditions</em>
        </>
      }
      intro="Standard Terms & Most Important Terms and Conditions (MITC) for the Loan Facility provided by Sahyog Finance, Vyara. Please read these terms carefully before submitting an Application Form or accepting any disbursement."
      sections={SECTIONS}
      lastUpdated="June 2026"
    />
  );
}
